import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

type Tone = 'success' | 'error' | 'info'

interface Toast {
  id: number
  tone: Tone
  message: string
}

interface ToastState {
  notify: (message: string, tone?: Tone) => void
  /** Runs a promise, reporting the outcome. Returns whether it succeeded. */
  attempt: (
    action: () => Promise<unknown>,
    messages: { success: string; failure?: string },
  ) => Promise<boolean>
}

const ToastContext = createContext<ToastState | null>(null)

// Errors stay up longer — there is usually something to read and act on.
const LIFETIME: Record<Tone, number> = {
  success: 3200,
  info: 4000,
  error: 7000,
}

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const notify = useCallback(
    (message: string, tone: Tone = 'success') => {
      const id = nextId++
      setToasts((current) => [...current, { id, tone, message }])
      window.setTimeout(() => dismiss(id), LIFETIME[tone])
    },
    [dismiss],
  )

  const attempt = useCallback<ToastState['attempt']>(
    async (action, messages) => {
      try {
        await action()
        notify(messages.success, 'success')
        return true
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error)
        notify(messages.failure ? `${messages.failure}: ${detail}` : detail, 'error')
        return false
      }
    },
    [notify],
  )

  const value = useMemo(() => ({ notify, attempt }), [notify, attempt])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Polite, so a screen reader finishes its sentence before announcing. */}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.tone}`}>
            <span className="bar" aria-hidden="true" />
            <span className="grow">{toast.message}</span>
            <button
              className="btn ghost sm"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastState {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside <ToastProvider>')
  return context
}
